import { ShareRoomFragment } from "~/__generated__/graphql"
import { cn } from "~/common/cn"
import { AppText } from "~/ui/app-text"
import { useRef, useEffect, useState, useMemo } from "react"
import pluralize from "pluralize"
import { RoomContentCardDescription } from "./components/room-content-card/description"
import { HostPill } from "./components/room-content-card/host-pill"
import { CaretLeft, CaretRight } from "phosphor-react"

interface RoomContentCardProps {
  roomData: ShareRoomFragment
  className?: string
  compact?: boolean
}

export const RoomContentCard = ({
  roomData,
  className,
  compact,
}: RoomContentCardProps): JSX.Element => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  // Function to update scroll arrow visibility
  const updateScrollArrows = () => {
    const container = scrollContainerRef.current
    if (!container) return

    const { scrollLeft, scrollWidth, clientWidth } = container
    setCanScrollLeft(scrollLeft > 0)
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth)
  }

  // Function to scroll by one HostPill width
  const scrollByHostPill = (direction: "left" | "right") => {
    const container = scrollContainerRef.current
    if (!container) return

    // Estimate HostPill width (adjust based on your HostPill component size)
    const hostPillWidth = 160 // Approximate width including gap
    const scrollAmount = direction === "left" ? -hostPillWidth : hostPillWidth

    container.scrollBy({
      left: scrollAmount,
      behavior: "smooth",
    })
  }

  useEffect(() => {
    const slider = scrollContainerRef.current
    if (!slider) return

    // Initial check for scroll arrows
    updateScrollArrows()

    let isDown = false
    let startX: number
    let scrollLeft: number
    let isDragging = false

    const handleMouseDown = (e: MouseEvent) => {
      isDown = true
      e.preventDefault()
      e.stopPropagation()
      slider.classList.add("cursor-grabbing")
      startX = e.pageX - slider.offsetLeft
      scrollLeft = slider.scrollLeft
      isDragging = false
    }

    const handleMouseLeave = (e: MouseEvent) => {
      if (!isDown) return
      isDown = false
      e.preventDefault()
      e.stopPropagation()
      slider.classList.remove("cursor-grabbing")
    }

    const handleMouseUp = (e: MouseEvent) => {
      if (!isDown) return
      isDown = false
      e.preventDefault()
      e.stopPropagation()
      slider.classList.remove("cursor-grabbing")
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDown) return
      e.preventDefault()
      e.stopPropagation()
      const x = e.pageX - slider.offsetLeft
      const walk = (x - startX) * 2 // Scroll speed multiplier
      slider.scrollLeft = scrollLeft - walk

      // If we've moved more than a few pixels, consider it a drag
      if (Math.abs(slider.scrollLeft - scrollLeft) > 5) {
        isDragging = true
      }
    }

    // Capture and suppress all click events during dragging
    const handleClick = (e: MouseEvent) => {
      if (isDragging) {
        e.preventDefault()
        e.stopPropagation()
        isDragging = false
      }
    }

    // Handle scroll events to update arrow visibility
    const handleScroll = () => {
      updateScrollArrows()
    }

    slider.addEventListener("mousedown", handleMouseDown)
    slider.addEventListener("mouseleave", handleMouseLeave)
    slider.addEventListener("mouseup", handleMouseUp)
    slider.addEventListener("mousemove", handleMouseMove)
    slider.addEventListener("click", handleClick, true)
    slider.addEventListener("scroll", handleScroll)

    return () => {
      slider.removeEventListener("mousedown", handleMouseDown)
      slider.removeEventListener("mouseleave", handleMouseLeave)
      slider.removeEventListener("mouseup", handleMouseUp)
      slider.removeEventListener("mousemove", handleMouseMove)
      slider.removeEventListener("click", handleClick, true)
      slider.removeEventListener("scroll", handleScroll)
    }
  }, [])

  const roomStatsText = useMemo(() => {
    const membersCount = roomData.activeRoomFollowsCount

    if (!membersCount || membersCount < 100) {
      return "Hosted by:"
    }

    const hostsCount = roomData.activeRoomMembershipsCount

    const formattedHostsCount = hostsCount?.toLocaleString() ?? 0
    const formattedMembersCount = membersCount?.toLocaleString()

    const hostsText = `${formattedHostsCount} ${pluralize("host", hostsCount)}`
    const membersText = membersCount
      ? ` · ${formattedMembersCount} ${pluralize("member", membersCount)}`
      : ""

    return `${hostsText}${membersText}`
  }, [roomData.activeRoomMembershipsCount, roomData.activeRoomFollowsCount])

  return (
    <div className={cn("flex w-full flex-col pt-4", className)}>
      <div className="flex flex-col items-center gap-4 px-4">
        {/* Avatar */}
        {roomData.photoUrl && (
          <img
            src={roomData.photoUrl}
            className={cn(
              "aspect-square w-[96px] rounded-[16px] border-[1px] border-base-white object-contain"
            )}
            alt="Room Avatar"
          />
        )}

        {/* Title */}
        <AppText
          variant={compact ? "h3" : "h2"}
          className={cn("text-base-white", compact ? "text-[21px]" : "text-[28px]")}
        >
          {roomData.name}
        </AppText>

        {/* Description */}
        <RoomContentCardDescription
          description={roomData.description || ""}
          compact={!!compact}
          isExpanded={isDescriptionExpanded}
          onExpandChange={setIsDescriptionExpanded}
          className="text-center"
        />

        {/* Host Grid */}
        <AppText
          variant={compact ? "body3-medium" : "body2-medium"}
          className={cn("mt-2 text-base-white")}
        >
          {roomStatsText}
        </AppText>
      </div>

      <div className="relative flex flex-row justify-center">
        {/* Left Arrow */}
        {canScrollLeft && (
          <button
            onClick={() => scrollByHostPill("left")}
            className="absolute left-[-44px] top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-base-black bg-opacity-white text-black transition-all hover:bg-black/25 max-lg:hidden"
            aria-label="Scroll left"
          >
            <CaretLeft size={16} />
          </button>
        )}

        {/* Right Arrow */}
        {canScrollRight && (
          <button
            onClick={() => scrollByHostPill("right")}
            className="absolute right-[-44px] top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-base-black bg-opacity-white text-black transition-all hover:bg-black/25 max-lg:hidden"
            aria-label="Scroll right"
          >
            <CaretRight size={16} />
          </button>
        )}

        <div
          ref={scrollContainerRef}
          className={cn(
            "flex cursor-grab select-none flex-row justify-start overflow-x-auto scroll-smooth py-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          )}
        >
          <div className={cn("mx-[16px] flex flex-row gap-2")}>
            {roomData.roomMembershipsPreview
              .map((m) => m.user)
              .slice(0, 100)
              .map((member, index) => (
                <HostPill key={index} member={member} />
              ))}
          </div>
        </div>
      </div>
    </div>
  )
}
